import { useState } from "react";
import { toast } from "sonner";
import { ImageIcon, Images, Layers, Loader2, RefreshCw, Save } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import AiGalleryGrid from "@/components/AiGalleryGrid";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import ImageStyleOptions, { saveBrandStyle, saveImgStyle, styleBody } from "@/components/composer/ImageStyleOptions";
import api, { errMsg } from "@/lib/api";

// "Tajuk utama" + Gemini image: simple illustration with only the headline text, in the chosen style/colours.
export default function AiImageGen({ title, setTitle, imageId, setImageId, imgStyle, setImgStyle, vary, setVary, groupCount }) {
  const [busy, setBusy] = useState(false);
  const [gallery, setGallery] = useState(false);
  const [lastAi, setLastAi] = useState(null);
  const changeStyle = (s) => { setImgStyle(s); saveImgStyle(s); };
  const [savingBrand, setSavingBrand] = useState(false);
  const saveBrand = async () => {
    setSavingBrand(true);
    try { await saveBrandStyle(imgStyle); toast.success("Disimpan sebagai gaya jenama (semua peranti)"); } catch (e) { toast.error(errMsg(e)); } finally { setSavingBrand(false); }
  };

  const generate = async () => {
    if (title.trim().length < 2) return toast.error("Isi tajuk utama dahulu");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/image", { title: title.trim(), ...styleBody(imgStyle) });
      setImageId(data.id);
      setLastAi(data.id);
      toast.success("Gambar AI dijana & dilampirkan");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const regen = lastAi && imageId === lastAi;
  return (
    <div className="mb-4 space-y-2" data-testid="composer-ai-image">
      <label className="text-sm font-medium" htmlFor="pp-title">Tajuk utama</label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input id="pp-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="cth: Jualan Murah Kek Coklat RM25" className="bg-background" data-testid="composer-title-input" />
        <Button type="button" onClick={generate} disabled={busy || title.trim().length < 2} className="shrink-0 bg-violet-600 hover:bg-violet-500" data-testid="composer-ai-image-btn">
          {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : regen ? <RefreshCw className="mr-1.5 h-4 w-4" /> : <ImageIcon className="mr-1.5 h-4 w-4" />}
          {busy ? "Menjana gambar..." : regen ? "Jana semula" : "Jana gambar AI"}
        </Button>
        <Button type="button" variant="secondary" onClick={() => setGallery(true)} className="shrink-0" data-testid="composer-ai-gallery-btn"><Images className="mr-1.5 h-4 w-4" /> Dari galeri</Button>
      </div>
      <Dialog open={gallery} onOpenChange={setGallery}>
        <DialogContent className="max-h-[85vh] max-w-4xl overflow-y-auto border-border bg-card" data-testid="composer-ai-gallery-dialog">
          <DialogHeader><DialogTitle>Galeri Gambar AI</DialogTitle><DialogDescription>Pilih gambar AI lama untuk guna semula dalam post ini.</DialogDescription></DialogHeader>
          <AiGalleryGrid selectedId={imageId} onPick={(it) => { setImageId(it.id); setTitle(it.title); setGallery(false); toast.success("Gambar dari galeri dilampirkan"); }} />
        </DialogContent>
      </Dialog>
      <ImageStyleOptions value={imgStyle} onChange={changeStyle} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">AI lukis ilustrasi ringkas dengan teks tajuk sahaja (10-30 saat), ikut gaya & tema warna pilihan.</p>
        <Button type="button" size="sm" variant="ghost" onClick={saveBrand} disabled={savingBrand} className="h-7 text-xs text-violet-300" data-testid="composer-save-brand-style-btn">
          {savingBrand ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1 h-3.5 w-3.5" />} Simpan sebagai gaya jenama
        </Button>
      </div>
      {imageId && title.trim().length >= 2 && (
        <label className="flex items-center justify-between gap-3 rounded-lg border border-violet-500/30 bg-violet-500/5 p-3" data-testid="composer-image-vary-row">
          <div>
            <p className="flex items-center gap-1.5 text-sm font-medium"><Layers className="h-3.5 w-3.5 text-violet-300" /> Gambar berbeza setiap group</p>
            <p className="text-xs text-muted-foreground">
              {groupCount > 1 ? `AI cipta ${groupCount - 1} versi sedikit berbeza dari gambar ini di latar selepas simpan (1 gambar AI setiap group).` : "Pilih 2 group atau lebih untuk guna pilihan ini."}
            </p>
          </div>
          <Switch checked={vary} onCheckedChange={setVary} disabled={groupCount < 2} data-testid="composer-image-vary-toggle" />
        </label>
      )}
    </div>
  );
}
